import * as babylon from "@babylonjs/core";

import * as components from "../../components";

/**
 * Applies — or clears — one entity's `pointSize` without touching the shared
 * template.
 *
 * The loader's template shares its materials with every copy of a file, so a
 * size written onto them would be *every* entity's size: the last writer would
 * win, and an entity that never set one would inherit a sibling's. Instead the
 * first override clones the point material onto this copy's own mesh and sets
 * the size there; a later edit mutates the clone in place; clearing the field
 * puts the shared material back and disposes the clone. The template therefore
 * always carries the default, which is what "absent means default" costs.
 *
 * Idempotent and cheap enough to call on every spec change: with no override
 * and none recorded it touches nothing.
 *
 * Only real `Mesh`es are dressed. An `InstancedMesh` — a file pointing two
 * nodes at one mesh — has a warn-and-ignore `material` setter and always
 * renders its source's material, so it follows the source's clone on its own.
 */
export function applyPointSizeOverride(
  model: components.Model,
  pointSize: number | undefined,
): void {
  const overrides = (model.pointSizeOverrides ??= new Map<
    babylon.TransformNode,
    babylon.Material
  >());

  if (pointSize === undefined) {
    for (const [node, original] of overrides) {
      const clone = (node as babylon.AbstractMesh).material;
      (node as babylon.AbstractMesh).material = original;
      clone?.dispose();
    }
    overrides.clear();
    return;
  }

  for (const node of model.nodesByName.values()) {
    if (node.getClassName() !== "Mesh") {
      continue;
    }
    const mesh = node as babylon.AbstractMesh;
    const material = mesh.material;
    if (!material?.pointsCloud) {
      continue;
    }
    if (!overrides.has(node)) {
      const clone = material.clone(`${material.name}.${model.anchor.name}`);
      if (clone === null) {
        continue;
      }
      overrides.set(node, material);
      mesh.material = clone;
    }
    // Non-null by construction: an override entry always leaves a clone on the
    // mesh, and a fresh clone was just assigned above.
    mesh.material!.pointSize = pointSize;
  }
}

/**
 * Releases the clones an entity's overrides created, restoring nothing: the
 * copy they dress is being torn down with them.
 */
export function disposePointSizeOverrides(model: components.Model): void {
  if (!model.pointSizeOverrides) {
    return;
  }
  for (const node of model.pointSizeOverrides.keys()) {
    (node as babylon.AbstractMesh).material?.dispose();
  }
  model.pointSizeOverrides.clear();
}
